import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Spinner from '@/components/shared/Spinner';
import { usePanelOpenClose } from '@/features/Quote/hooks/usePanelOpenClose';
import { isSourceTrusted } from '@/features/Quote/services/confirmationWizard';
import { isFeatureActive } from '@/lib/featureFlags';
import apiService from '@/services/apiService';
import { prepareExtractedEmployees } from '@/services/utils';
import type { Employee, ExtractionResponse, SettingType } from '@/types/app';
import { FeatureFlag } from '@/types/app';
import DetailCard from '../DetailCard';
import Import from './Import';
import Introduction from './Introduction';
import Employees from './List';

type ViewMode = 'loading' | 'introduction' | 'grid' | 'import';

interface DetailsProps {
	activeSetting: SettingType | null;
	setActiveSetting: (setting: SettingType | null) => void;
	handleTriggerAppStatePolling: () => void;
	openedFromQuoteSelection?: boolean;
	employeesSource: string | null;
	confirmedSteps: ReadonlySet<SettingType>;
	markStepConfirmed: (step: SettingType) => void;
}

const Details = ({
	activeSetting,
	setActiveSetting,
	handleTriggerAppStatePolling,
	openedFromQuoteSelection = false,
	employeesSource,
	confirmedSteps,
	markStepConfirmed
}: DetailsProps) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { config, appData, premiumVarManagement } = useAppContext();
	const [viewMode, setViewMode] = useState<ViewMode>('loading');
	const [showImportMode, setShowImportMode] = useState(false);
	const originalEmployeesRef = useRef<Employee[]>([]);
	const openSnapshotRef = useRef<Employee[] | null>(null);

	const allowExtraction = isFeatureActive(config, FeatureFlag.ALLOW_EMPLOYEE_EXTRACTION);

	// Decide which view to show when the panel opens (and snapshot the current list so a
	// cancel / click-outside can restore it). A trusted source — or the feature being off
	// — goes straight to the editable grid. For an untrusted source we normally show the
	// manual/upload introduction; but if the client already uploaded an employee file
	// (e.g. in the wage modal) and extraction completed, pre-fill the grid with those
	// employees instead of asking them to upload again. Recalculation happens when they
	// confirm. (issue #927)
	const handleOpen = useCallback(() => {
		openSnapshotRef.current = [...(appData?.premium_variables?.employees?.employee_list ?? [])];

		if (!allowExtraction || isSourceTrusted(employeesSource)) {
			setViewMode('grid');
			return;
		}
		if (!id) {
			setViewMode('introduction');
			return;
		}

		setViewMode('loading');
		apiService.import
			.fetch(id)
			.then((response) => {
				const extraction = response as ExtractionResponse;
				const extracted = extraction.result?.employee_list;
				if (extraction.status === 'completed' && extracted?.length) {
					premiumVarManagement.employees.replace(prepareExtractedEmployees(extracted));
					setViewMode('grid');
				} else {
					setViewMode('introduction');
				}
			})
			.catch(() => {
				// No extraction available (e.g. nothing uploaded yet) — show the manual/upload choice.
				setViewMode('introduction');
			});
	}, [
		appData?.premium_variables?.employees?.employee_list,
		allowExtraction,
		employeesSource,
		id,
		premiumVarManagement.employees
	]);

	const handleClose = useCallback(() => {
		if (!confirmedSteps.has('employees') && openSnapshotRef.current) {
			premiumVarManagement.employees.replace(openSnapshotRef.current);
		}
		// Reset so the next open starts from the loading state rather than a stale view.
		setViewMode('loading');
	}, [confirmedSteps, premiumVarManagement.employees]);

	usePanelOpenClose(activeSetting === 'employees', { onOpen: handleOpen, onClose: handleClose });

	const handleImportClick = () => {
		// Store current employees before importing
		const currentEmployees = appData?.premium_variables?.employees?.employee_list || [];
		originalEmployeesRef.current = [...currentEmployees];
		setViewMode('import');
	};

	const handleBackToGrid = () => {
		// When returning from import, enable import mode to show cancel button
		setShowImportMode(true);
		setViewMode('grid');
	};

	const handleManualEntry = () => {
		setViewMode('grid');
	};

	return (
		<DetailCard
			title={t('quotes.settings.employees.panelTitle')}
			dataAttribute="edit-employees-card"
			type="employees"
			isVisible={activeSetting === 'employees'}
			onClose={() => setActiveSetting(null)}
		>
			{viewMode === 'loading' && (
				<div className="flex justify-center py-10" data-testid="employees-loading-view">
					<Spinner size="md" visible={true} />
				</div>
			)}
			{viewMode === 'introduction' && allowExtraction && (
				<Introduction
					onManualEntry={handleManualEntry}
					onImportClick={handleImportClick}
					handleTriggerAppStatePolling={handleTriggerAppStatePolling}
					openedFromQuoteSelection={openedFromQuoteSelection}
				/>
			)}
			{viewMode === 'grid' && (
				<Employees
					setActiveSetting={setActiveSetting}
					onShowImport={handleImportClick}
					openedFromQuoteSelection={openedFromQuoteSelection}
					showImportMode={showImportMode}
					setShowImportMode={setShowImportMode}
					originalEmployeesRef={originalEmployeesRef}
					confirmedSteps={confirmedSteps}
					markStepConfirmed={markStepConfirmed}
				/>
			)}
			{viewMode === 'import' && allowExtraction && (
				<Import
					onImportComplete={handleBackToGrid}
					handleTriggerAppStatePolling={handleTriggerAppStatePolling}
				/>
			)}
		</DetailCard>
	);
};

export default Details;
